import {
	FileMetadataDetailsResponse,
	FileWithMetadataPayload,
	RegisterFileResponse,
} from "@dune/types";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { privateAxios } from "../axios";
import { filesQueryKeys } from "../query-keys/files.query-keys";

export const getFileUrlByBucketAndKey = async (
	bucket?: string,
	key?: string,
) => {
	const { data } = await privateAxios.get<string>(
		`/s3/get-file-url-by-bucket-and-key/${bucket}/${encodeURIComponent(key || "")}`,
	);
	return data;
};

export const useGetFileUrlByBucketAndKey = (bucket?: string, key?: string) => {
	return useQuery({
		queryKey: filesQueryKeys.fileUrlByBucketAndKey(bucket, key),
		queryFn: () => getFileUrlByBucketAndKey(bucket, key),
		enabled: !!bucket && !!key,
	});
};

export const useGetSignedPutUrlQuery = (
	bucket: string,
	contentType?: string,
) => {
	return useQuery({
		queryKey: filesQueryKeys.signedPutUrl(bucket, contentType),
		queryFn: () => getSignedPutUrl({ bucket, contentType }),
		enabled: false,
	});
};

const getSignedGetUrl = async (key: string) => {
	const { data } = await privateAxios.get<string>("/s3/get-private-file-url", {
		params: { key },
	});
	return data;
};

export const useGetSignedGetUrlQuery = (key: string) => {
	return useQuery({
		queryKey: filesQueryKeys.signedGetUrl(key),
		queryFn: () => getSignedGetUrl(key),
		enabled: false,
	});
};

export const getSignedPutUrl = async ({
	bucket,
	contentType,
}: { bucket: string; contentType?: string }) => {
	const { data } = await privateAxios.get<{
		signedUrl: string;
		key: string;
	}>(`/s3/get-signed-put-url/${bucket}`, {
		params: { contentType },
	});
	return data;
};

export const uploadFileToS3 = async (
	signedUrl: string,
	file: globalThis.File,
) => {
	await axios.put(signedUrl, file, {
		headers: { "Content-Type": file.type },
	});
};

export const registerS3File = async ({
	bucket,
	key,
	name,
	mimeType,
	size,
	folderId,
}: {
	bucket: string;
	key: string;
	name: string;
	mimeType: string;
	size?: number;
	folderId?: string;
}) => {
	const { data } = await privateAxios.post<RegisterFileResponse>(
		"/files/register-file",
		{
			bucket,
			key,
			name,
			mimeType,
			size,
			folderId,
		},
	);
	return data;
};

export const createDocumentTemplate = async ({
	name,
	entityModel,
	templateType,
	fileId,
	fileKey,
}: {
	name: string;
	entityModel: string;
	templateType: string;
	fileId: string;
	fileKey: string;
}) => {
	const { data } = await privateAxios.post("/documents/templates", {
		name,
		entityModel,
		templateType,
		fileId,
		fileKey,
	});
	return data;
};

export const getPrivateFileUrl = async (key: string): Promise<string> => {
	const { data } = await privateAxios.get<string>("/s3/get-private-file-url", {
		params: { key },
	});
	return data;
};

export const getFileWithMetadata = async (id: string) => {
	const { data } = await privateAxios.get<FileWithMetadataPayload>(
		`/files/${id}`,
	);
	return data;
};

export const useGetFileWithMetadata = (id: string) => {
	return useQuery({
		queryKey: filesQueryKeys.fileWithMetadata(id),
		queryFn: () => getFileWithMetadata(id),
		enabled: !!id,
	});
};

export const getFilesWithMetadata = async (ids: string[]) => {
	if (ids.length === 0) {
		return [];
	}
	const { data } = await privateAxios.post<FileWithMetadataPayload[]>(
		"/files/batch",
		{ ids },
	);
	return data;
};

export const useGetFilesWithMetadata = (ids: string[]) => {
	return useQuery({
		queryKey: filesQueryKeys.filesWithMetadata(ids),
		queryFn: () => getFilesWithMetadata(ids),
		enabled: ids.length > 0,
	});
};

export const folderHasAiJobRunning = async (folderId: string) => {
	const { data } = await privateAxios.get<boolean>(
		`/files/folder-has-ai-job-running/${folderId}`,
	);
	return data;
};

export const useFolderHasAiJobRunning = (folderId: string) => {
	return useQuery({
		queryKey: filesQueryKeys.folderHasAiJobRunning(folderId),
		queryFn: () => folderHasAiJobRunning(folderId),
		enabled: !!folderId,
	});
};

export const getPublicFileUrl = (bucketName: string, key: string): string => {
	return `https://${bucketName}.s3.eu-west-3.amazonaws.com/${key}`;
};

export const getDocumentUrl = async ({
	bucket,
	key,
}: { bucket: string; key: string }): Promise<string> => {
	if (bucket === "private") {
		return await getPrivateFileUrl(key);
	}
	return getPublicFileUrl(bucket, key);
};

export const useGetDocumentUrl = (
	{
		bucket,
		key,
		documentId,
	}: { bucket: string; key: string; documentId: string },
	enabled = false,
) => {
	return useQuery({
		queryKey: ["document-url", documentId, bucket, key],
		queryFn: () => getDocumentUrl({ bucket, key }),
		enabled,
		staleTime: 5 * 60 * 1000,
		gcTime: 10 * 60 * 1000,
	});
};

export const getFileMetadataDetails = async (
	id: string,
): Promise<FileMetadataDetailsResponse> => {
	const { data } = await privateAxios.get<FileMetadataDetailsResponse>(
		`/files/${id}/metadata-details`,
	);
	return data;
};

export const useGetFileMetadataDetails = (
	id: string,
	options?: { enabled?: boolean },
) => {
	return useQuery<FileMetadataDetailsResponse>({
		queryKey: filesQueryKeys.fileMetadataDetails(id),
		queryFn: () => getFileMetadataDetails(id),
		enabled: options?.enabled ?? !!id,
	});
};
